import { usePersistedState } from "@/lib/persistedFilters";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { errMsg } from "../ui";
import { CallDetailDialog } from "./CallDetailDialog";
import { CallsTable } from "./CallsTable";
import { money, num } from "./meta";

const PAGE = 50;
const KEYS = ["start", "end", "territory", "domain", "q", "charged"];

function Field({ label, children }) {
  return <label className="space-y-1"><span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>{children}</label>;
}

function Filters({ f, setF, onSearch, busy, maxDay }) {
  const inp = (k, ph, w = "w-44") => <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} onKeyDown={(e) => e.key === "Enter" && onSearch()} placeholder={ph} className={`h-9 ${w} text-xs`} data-testid={`uc-calls-${k}`} />;
  return (
    <div className="flex flex-wrap items-end gap-3 border-b border-slate-100 px-4 py-3" data-testid="uc-calls-filters">
      <Field label="From (UTC)"><Input type="date" value={f.start} max={maxDay} onChange={(e) => setF({ ...f, start: e.target.value })} className="h-9 w-40" data-testid="uc-calls-start" /></Field>
      <Field label="To (UTC)"><Input type="date" value={f.end} max={maxDay} onChange={(e) => setF({ ...f, end: e.target.value })} className="h-9 w-40" data-testid="uc-calls-end" /></Field>
      <Field label="Territory">{inp("territory", "any")}</Field>
      <Field label="Domain">{inp("domain", "any", "w-52")}</Field>
      <Field label="Number or extension">{inp("q", "ANI, dialled, ext…")}</Field>
      <label className="flex h-9 items-center gap-2 text-xs text-slate-700"><Checkbox checked={f.charged} onCheckedChange={(v) => setF({ ...f, charged: !!v })} data-testid="uc-calls-charged" /> Charged calls only</label>
      <Button onClick={onSearch} disabled={busy} data-testid="uc-calls-search">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search</Button>
    </div>
  );
}

/** Browse the calls saved in the billing database (ucbilling CDR table). */
export function CallBrowser({ ov }) {
  const [sp, setSp] = useSearchParams();
  const init = () => ({ start: sp.get("start") || ov.open_cycle.start, end: sp.get("end") || ov.open_cycle.through,
    territory: sp.get("territory") || "", domain: sp.get("domain") || "", q: sp.get("q") || "", charged: sp.get("charged") === "1" });
  const fromUrl = ["start", "end", "territory", "domain", "q", "charged"].some((k) => sp.get(k)) ? init() : undefined;
  const [f, setF] = usePersistedState("usage-calls.form", init, fromUrl);
  const [applied, setApplied] = usePersistedState("usage-calls.applied", init, fromUrl);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const closeCall = useCallback(() => setOpenId(null), []);
  const params = (x) => ({ ...x, charged: x.charged ? "true" : "false" });
  const load = useCallback(() => {
    setBusy(true);
    api.get("/usage-charges/calls", { params: { ...params(applied), page, page_size: PAGE } })
      .then(({ data: d }) => setData(d)).catch((e) => { setData(null); toast.error(errMsg(e, "Could not load the calls")); })
      .finally(() => setBusy(false));
  }, [applied, page]);
  useEffect(() => { load(); }, [load]);
  const search = () => {
    setPage(1); setApplied({ ...f });
    setSp(Object.fromEntries(KEYS.filter((k) => f[k]).map((k) => [k, k === "charged" ? "1" : f[k]])), { replace: true });
  };
  const csv = `${API}/usage-charges/calls.csv?${new URLSearchParams(params(applied)).toString()}`;
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;
  return (
    <Card className="overflow-hidden border-slate-200 shadow-sm" data-testid="uc-call-browser">
      <Filters f={f} setF={setF} onSearch={search} busy={busy} maxDay={ov.utc_today} />
      <div className="flex flex-wrap items-center gap-4 border-b border-slate-100 bg-slate-50/60 px-4 py-2 text-xs text-slate-600" data-testid="uc-calls-summary">
        {data ? (<><span><b className="font-mono tabular-nums text-slate-900" data-testid="uc-calls-total">{num(data.total)}</b> calls</span>
          <span><b className="font-mono tabular-nums text-slate-900">{num(data.charged)}</b> charged</span>
          <span>Inbound Revenue <b className="font-mono tabular-nums text-slate-900" data-testid="uc-calls-revenue">{money(data.revenue)}</b></span></>) : <span>—</span>}
        {ov.can_export && data?.total > 0 && <a href={csv} className="ml-auto inline-flex items-center gap-1 font-medium text-emerald-700 hover:underline" data-testid="uc-calls-export"><Download className="h-3.5 w-3.5" /> Export CSV (all columns)</a>}
      </div>
      <div className="max-h-[62vh] overflow-auto">
        {!data ? <Loader2 className="mx-auto my-10 h-5 w-5 animate-spin text-slate-300" /> : !data.rows.length
          ? <p className="p-10 text-center text-sm text-slate-500" data-testid="uc-calls-empty">No calls saved for these filters. Days that were never collected can be fetched from WebCDR on the Runs &amp; cycle tab.</p>
          : <CallsTable rows={data.rows} onOpen={setOpenId} />}
      </div>
      {data?.total > PAGE && (
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-2 text-xs text-slate-500">
          Page {page} of {num(pages)}
          <Button size="sm" variant="ghost" disabled={page <= 1 || busy} onClick={() => setPage(page - 1)} data-testid="uc-calls-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" disabled={page >= pages || busy} onClick={() => setPage(page + 1)} data-testid="uc-calls-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>)}
      <CallDetailDialog id={openId} onClose={closeCall} />
    </Card>
  );
}
